import { inject, Injectable } from "@angular/core";
import { TradeKey } from "../../model/api";
import { RestClientFactory } from "../../market/rest-client-factory";
import { WebsocketClientFactory } from "../../market/websocket-client-factory";
import { CreateOrderParams, CreateStopOrderParams, Order } from "../../model/trade";
import { BybitOrderManager } from "./exchange/bybit-order.manager";
import { BinanceUsdmOrderManager } from "./exchange/binance-order.manager";
import { OkxOrderManager } from "./exchange/okx-order.manager";
import { LogEntryFactory, LoggerService } from "../../service/logger/logger.service";
import { catchError, Observable } from "rxjs";
import { MarketError } from "../../error/codes";

@Injectable({
  providedIn: 'root'
})
export class OrderManagerService {

  private rcf = inject(RestClientFactory)
  private wcf = inject(WebsocketClientFactory)
  private logger = inject(LoggerService)
  private logFactory = inject(LogEntryFactory)
  private bybitManager = inject(BybitOrderManager)
  private binanceUsdmManager = inject(BinanceUsdmOrderManager)
  private okxManager = inject(OkxOrderManager)

  createOrder(key: TradeKey, order: CreateOrderParams): Observable<unknown> {
    if (key.market == 'bybit') {
      const ws = this.wcf.createForKey(key).bybit
      return this.bybitManager.createOrder(ws, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'binance') {
      const rest = this.rcf.createForKey(key).binanceUsdm
      return this.binanceUsdmManager.createOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'okx') {
      const rest = this.rcf.createForKey(key).okx
      return this.okxManager.createOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else {
      throw new Error(`Unknown market ${key.market}`)
    }
  }

  createStopOrder(key: TradeKey, order: CreateStopOrderParams): Observable<unknown> {
    if (key.market == 'bybit') {
      const rest = this.rcf.createForKey(key).bybit
      //const ws = this.wcf.createForKey(key).bybit
      return this.bybitManager.createStopOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'binance') {
      const rest = this.rcf.createForKey(key).binanceUsdm
      return this.binanceUsdmManager.createStopOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'okx') {
      const rest = this.rcf.createForKey(key).okx
      return this.okxManager.createStopOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else {
      throw new Error(`Unknown market ${key.market}`)
    }
  }

  cancelOrder(key: TradeKey, order: Order): Observable<unknown> {
    if (key.market == 'bybit') {
      const ws = this.wcf.createForKey(key).bybit
      return this.bybitManager.cancelOrder(ws, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'binance') {
      const rest = this.rcf.createForKey(key).binanceUsdm
      return this.binanceUsdmManager.cancelOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else if (key.market == 'okx') {
      const rest = this.rcf.createForKey(key).okx
      return this.okxManager.cancelOrder(rest, order).pipe(
        catchError(err => this.handleError(key, order.symbol, err))
      )
    }
    else {
      throw new Error(`Unknown market ${key.market}`)
    }
  }

  cancelOrders(key: TradeKey, orders: Order[]): Observable<unknown> {
    if (key.market == 'bybit') {
      const ws = this.wcf.createForKey(key).bybit
      return this.bybitManager.cancelOrders(ws, orders).pipe(
        catchError(err => this.handleError(key, undefined, err))
      )
    }
    else if (key.market == 'binance') {
      const rest = this.rcf.createForKey(key).binanceUsdm
      return this.binanceUsdmManager.cancelOrders(rest, orders).pipe(
        catchError(err => this.handleError(key, undefined, err))
      )
    }
    else if (key.market == 'okx') {
      const rest = this.rcf.createForKey(key).okx
      return this.okxManager.cancelOrders(rest, orders).pipe(
        catchError(err => this.handleError(key, undefined, err))
      )
    }
    else {
      throw new Error(`Unknown market ${key.market}`)
    }
  }

  private handleError(key: TradeKey, symbol: string | undefined, err: any): never {
    if (err instanceof MarketError) {
      const entry = this.logFactory.marketError(key, err)
      if (symbol) {
        entry.addTags(`symbol.${symbol}`)
      }
      this.logger.getKeyLogger(key).add(entry)
    }
    throw err
  }

}
